import get from 'lodash/get';
import { Menu, Layout } from 'antd';
import { Component, createRef, useState, useEffect } from 'react';
import { withRouter, Link } from 'react-router-dom';
import { useApolloClient, useQuery } from '@apollo/client';
import { COLORS } from 'src/_shared/styles/colors';
import { USER_ROLES } from '../../constants/userRolesEnum.js';
import { ml } from '../../services/utils';
import { isPhone } from 'src/_shared/phone.js';
import { getHeaderNavItems } from './headerNavItems.js';
import { UserProfile } from './user-profile';
import { hasPermission } from 'src/_shared/components/permission-gate/pathHelpers.js';
import {
	GetCompanyAnnouncementsSettings,
	queryAnnouncements,
} from 'src/new-announcements/graphqlQueries.js';

const { Header } = Layout;
const REVEAL_SPEED = 1; // px per ms of upward scroll that brings the header back

class BaseHeader extends Component {
	constructor(props) {
		super(props);
		this.state = {
			headerMultiData: '',
			currentLocation: this.props?.location?.pathname || '/',
			hidden: false,
		};
		this.headerRef = createRef();
		this.scroller = null;
		this.lastScrollTop = 0;
		this.lastScrollTime = 0;
		this.scrollFrame = null;
	}

	async componentDidMount() {
		const pathname =
			this.props.location.pathname === '/'
				? '/dashboard'
				: this.props.location.pathname;
		this.props.updatePathname(pathname);
		this.watchScroll();
	}

	async componentDidUpdate(prevProps) {
		if (prevProps.currentLocation !== this.props.currentLocation) {
			this.setState({ currentLocation: this.props.currentLocation });
		}
		this.watchScroll();
	}

	componentWillUnmount() {
		this.scroller?.removeEventListener('scroll', this.handleScroll);
		if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
	}

	// Phone widths: the header is sticky (see _shell.scss) and slides out of
	// the way while scrolling down, back in on a quick scroll up. The page
	// scrolls inside the header's parent (.container-fluid).
	watchScroll = () => {
		if (this.scroller || !isPhone()) return;
		const scroller = this.headerRef.current?.parentElement;
		if (!scroller) return;
		this.scroller = scroller;
		this.lastScrollTop = scroller.scrollTop;
		scroller.addEventListener('scroll', this.handleScroll, { passive: true });
	};

	// Scrolling down hides it; a quick flick up (REVEAL_SPEED px/ms) or
	// reaching the top shows it. A slow drag up leaves it hidden.
	handleScroll = () => {
		if (this.scrollFrame) return;
		this.scrollFrame = requestAnimationFrame(() => {
			this.scrollFrame = null;
			const now = performance.now();
			const top = this.scroller.scrollTop;
			const delta = top - this.lastScrollTop;
			const speed = Math.abs(delta) / Math.max(now - this.lastScrollTime, 1);
			this.lastScrollTop = top;
			this.lastScrollTime = now;
			let { hidden } = this.state;
			if (top <= 0) hidden = false;
			else if (delta > 4 && top > 80) hidden = true;
			else if (delta < -4 && speed >= REVEAL_SPEED) hidden = false;
			if (hidden !== this.state.hidden) this.setState({ hidden });
		});
	};

	handleLinkClick = (location) => {
		this.props.updatePathname(location);
	};

	getSelectedKeys = () => {
		const currentPathname = this.props?.location?.pathname || '/';
		const menuItems = this.renderMenuItems();

		for (const item of menuItems) {
			const link = item.label?.props?.to;
			if (link && currentPathname.startsWith(link)) {
				return [item.key];
			}
		}

		return [];
	};

	renderMenuItems = () => {
		const allMultiLingualData = get(this.props, 'allMultiLingualData');
		const currentUser = get(this.props, 'currentUser');
		const items = getHeaderNavItems({
			currentUser,
			customPageData: this.props?.customPageData,
			announcementCount: this.props.announcementCount,
			allMultiLingualData,
		}).map(({ key, title, link }) => ({
			label: <Link to={link}>{title}</Link>,
			key,
		}));

		if (currentUser?.isScopedUser) {
			let scopedPolicy = currentUser?.scopedPolicy || {};
			if (typeof scopedPolicy === 'string') {
				try {
					scopedPolicy = JSON.parse(scopedPolicy);
				} catch (e) {
					scopedPolicy = {};
					console.error(e);
				}
			}
			const allowedPaths = scopedPolicy?.paths || [];

			if (allowedPaths.length > 0) {
				return items.filter((item) => {
					const link = item.label?.props?.to;
					if (!link) return false;
					return hasPermission(link, allowedPaths);
				});
			}
		}

		return items;
	};

	render() {
		const {
			allMultiLingualData,
			createSetLanguage,
			onUpdate,
			setMultiLingualData,
			updateCurrentUserLanguage,
			currentUser,
			toggleAdminControls,
			showAdminControls,
		} = this.props;
		const menuItems = this.renderMenuItems();
		return get(currentUser, 'company') ? (
			<div
				ref={this.headerRef}
				className={`row app-header${this.state.hidden ? ' app-header-hidden' : ''}`}
				style={{
					backgroundColor: 'white',
					alignItems: 'center',
					flexWrap: 'nowrap',
					height: '65px',
					borderBottom: '1px solid #f0f0f0',
				}}
			>
				<div
					className="hamburger"
					onClick={this.props.hamburgerClickHandler}
					style={{ marginLeft: '15px', marginRight: 0 }}
				>
					<div className="bar" />
					<div className="bar" />
					<div className="bar" />
				</div>
				<nav
					className="nav-menu"
					style={{ width: 'calc(100% - 308px', marginLeft: 'auto' }}
				>
					<Header
						style={{
							padding: 0,
							fontSize: '14px',
							fontWeight: 800,
							textTransform: 'uppercase',
							background: 'none',
						}}
					>
						<Menu
							mode="horizontal"
							items={menuItems}
							selectedKeys={this.getSelectedKeys()}
						/>
					</Header>
				</nav>
				<UserProfile
					currentUser={currentUser}
					history={this.props.history}
					updateCurrentUserLanguage={updateCurrentUserLanguage}
					createSetLanguage={createSetLanguage}
					allMultiLingualData={allMultiLingualData}
					setMultiLingualData={setMultiLingualData}
					onUpdate={onUpdate}
					toggleAdminControls={toggleAdminControls}
					showAdminControls={showAdminControls}
				/>
			</div>
		) : (
			<div style={{ height: 71, backgroundColor: COLORS.lightGray2 }} />
		);
	}
}

const MODERATOR_ROLES = [
	USER_ROLES.ADMIN,
	USER_ROLES.MANAGER,
	USER_ROLES.SUPER_ADMIN,
	USER_ROLES.SUPPORT_ADMIN,
];

export const HeaderComponent = withRouter((props) => {
	const client = useApolloClient();
	const [announcementCount, setAnnouncementCount] = useState(null);
	const currentUser = props.currentUser;
	const companyId = currentUser?.companyId;
	const userId = currentUser?.id;
	const displayAs = currentUser?.displayAs;
	const isModerator = MODERATOR_ROLES.includes(displayAs);

	const { data: settingsData } = useQuery(GetCompanyAnnouncementsSettings, {
		variables: { id: companyId },
		skip: !companyId,
	});
	const showCount = get(
		settingsData,
		'getCompany.announcementsSettings.showCount',
		false
	);

	useEffect(() => {
		if (!userId || !showCount) {
			setAnnouncementCount(null);
			return;
		}

		let cancelled = false;

		(async () => {
			try {
				const countResult = await client.query({
					query: queryAnnouncements,
					variables: {
						userId,
						overrideRole: !isModerator || undefined,
						first: 1,
					},
					fetchPolicy: 'network-only',
				});
				if (!cancelled) {
					const total = get(
						countResult,
						'data.queryAnnouncements.totalCount',
						0
					);
					setAnnouncementCount(total);
				}
			} catch (error) {
				console.error('Error fetching announcement count:', error);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [client, userId, isModerator, showCount]);

	return <BaseHeader {...props} announcementCount={announcementCount} />;
});
